/**
 * Forget the Record (D242) — the settings panel's one way to clear what a
 * game remembers across runs (D241).
 *
 * @remarks
 * A game that declares a Record carries the endings of earlier runs into the
 * next one, on this device and — signed in — in the player's account. The
 * player owns that memory and may throw it away; this is where. It sits in
 * the settings panel beside the game's other preferences, because it is
 * one: a choice about how THIS game treats the player, made rarely, never
 * mid-sentence. The shell renders it only for a pack that declares a Record
 * (`GameDescription.hasRecord`): a forget for a memory the game does not
 * keep would be a promise about nothing.
 *
 * It asks once, through the product's one destructive gate (D179's confirm
 * dialog), because what it clears has no copy. The answer is the host's —
 * `onForget` resolves when the device and, signed in, the account have let
 * go — and only then does the status line say so. A forget that fails
 * claims nothing: the same status line says, politely, that the Record
 * could not be cleared just now (D243, Calder's words), and the button is
 * ready again, so the player is never told a memory is gone that is still
 * there and never left wondering whether the press did anything.
 *
 * **Keyboard and screen readers.** The button is described by its hint.
 * Every way out of the confirm — Keep, Forget, Escape — puts focus back on
 * the button, so a keyboard player is never stranded on `body` when the
 * dialog leaves. Escape inside the confirm closes the confirm and nothing
 * else: the settings panel listens for Escape on the document, and without
 * the stop below one key would close both. While the host is forgetting,
 * the button is `aria-disabled` rather than `disabled`, because a disabled
 * button cannot hold focus and the focus would fall to `body` at the very
 * moment it was returned. The status line is always in the DOM, so its
 * sentence is announced as it arrives.
 *
 * Every word is Calder's (2026-10-09; the failure line 2026-10-10), held here as
 * {@link FORGET_RECORD_COPY} so the shell and the tests read the same
 * sentences.
 *
 * @packageDocumentation
 */

import { type KeyboardEvent, useId, useRef, useState } from 'react';

import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog-component.js';

/** The control's words (Calder's ruling on the D240 note, 2026-10-09). */
export const FORGET_RECORD_COPY = {
	/** The status line when the host could not let go (D243) — nothing is claimed, and the button is ready again. */
	couldNotForget: 'The Record could not be cleared just now. Try again in a moment.',
	/** The status line, once the host has let go. */
	forgotten: 'The Record is clear. Your next run starts fresh.',
	/** What the control clears, under its label — and the sentence the confirm asks. */
	hint: 'Clears what this game remembers of your earlier runs — on this device, and on your account if you are signed in.',
	/** The confirm's keeping answer. */
	keepLabel: 'Keep',
	/** The control's label. */
	label: 'Forget the Record',
	/** The confirm's irreversible answer. */
	proceedLabel: 'Forget',
} as const;

/** Props for {@link ForgetRecordControlComponent}. */
export interface ForgetRecordControlComponentProps {
	/** Forget the Record — resolves once the host has cleared it everywhere it is kept, rejects when it could not. */
	readonly onForget: () => Promise<void>;
}

/** Where the control is: at rest, asking, waiting on the host, done — or (D243) refused, and ready to be asked again. */
type ForgetPhase = 'asking' | 'failed' | 'forgetting' | 'forgotten' | 'resting';

/**
 * What the status line says in each phase: the host's answer, once there is
 * one, and nothing while the control rests, asks or waits.
 *
 * @param phase - Where the control is
 * @returns The sentence, or nothing
 */
const statusLineOf = (phase: ForgetPhase): string | undefined => {
	if (phase === 'forgotten') {
		return FORGET_RECORD_COPY.forgotten;
	}

	return phase === 'failed' ? FORGET_RECORD_COPY.couldNotForget : undefined;
};

/**
 * Keep an Escape pressed inside the confirm from reaching the document, where
 * the settings panel would read it as "close the panel" too.
 *
 * @param keyboardEvent - The key pressed inside the confirm
 */
const keepEscapeInsideTheConfirm = (keyboardEvent: KeyboardEvent<HTMLDivElement>): void => {
	if (keyboardEvent.key === 'Escape') {
		keyboardEvent.stopPropagation();
	}
};

/**
 * Render the control: the button, what it clears, the confirm while it asks,
 * and the line that says it is done.
 *
 * @param props - {@link ForgetRecordControlComponentProps}
 * @returns The control
 */
export const ForgetRecordControlComponent = ({ onForget }: ForgetRecordControlComponentProps) => {
	const [phase, setPhase] = useState<ForgetPhase>('resting');
	const hintId = useId();
	const triggerElementRef = useRef<HTMLButtonElement>(null);
	const isForgetting = phase === 'forgetting';

	/** Back to the button — every way out of the confirm ends here. */
	const returnFocusToTrigger = (): void => {
		triggerElementRef.current?.focus();
	};

	/**
	 * The confirm was kept: Keep, Escape, or the dialog's own close. Only an
	 * open confirm can answer: the dialog exists only while the control is
	 * asking, and React 19 detaches an unmounted dialog's `onClose` before its
	 * ref cleanup closes it, so the close a Forget causes never lands here.
	 */
	const keep = (): void => {
		setPhase('resting');
		returnFocusToTrigger();
	};

	const forget = (): void => {
		setPhase('forgetting');
		returnFocusToTrigger();
		onForget().then(
			() => {
				setPhase('forgotten');
			},
			() => {
				setPhase('failed');
			},
		);
	};

	return (
		<div className="forget-record">
			<button
				aria-describedby={hintId}
				aria-disabled={isForgetting ? true : undefined}
				className="forget-record__trigger"
				onClick={() => {
					if (!isForgetting) {
						setPhase('asking');
					}
				}}
				ref={triggerElementRef}
				type="button"
			>
				{FORGET_RECORD_COPY.label}
			</button>
			<p className="forget-record__hint" id={hintId}>
				{FORGET_RECORD_COPY.hint}
			</p>
			{/* Always present, so a screen reader hears the line arrive rather than find it later. */}
			<p aria-live="polite" className="forget-record__done" role="status">
				{statusLineOf(phase)}
			</p>
			{phase === 'asking' ? (
				// eslint-disable-next-line jsx-a11y/no-static-element-interactions -- not a control: it only keeps the confirm's own Escape from closing the panel around it
				<div className="forget-record__confirm" onKeyDown={keepEscapeInsideTheConfirm}>
					<ConfirmDialogComponent
						keepLabel={FORGET_RECORD_COPY.keepLabel}
						onKeep={keep}
						onProceed={forget}
						proceedLabel={FORGET_RECORD_COPY.proceedLabel}
						sentence={FORGET_RECORD_COPY.hint}
					/>
				</div>
			) : undefined}
		</div>
	);
};
